//* Importing libries from react
import { useState, useCallback, useEffect } from "react";

//* Importing libries from react-query
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Importing libries from teamAPI
import { teamAPI } from "./teamAPI";

//* Importing Pagination from services/stores/types
import { type Pagination } from "../../services/stores/types";

//* Importing types from types
import {
  type TeamDetails,
  type CreateTeamsInput,
  type UpdateTeamInput,
} from "./types/types";

export const useTeams = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "teams",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      teamAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const teams = data?.teams || [];
  const fetchedPagination = data?.pagination || pagination;

  // Fetch the totalItems from the response and update the pagination state
  useEffect(() => {
    if (fetchedPagination.totalItems !== pagination.totalItems) {
      setPagination((prev) => ({
        ...prev,
        totalItems: fetchedPagination.totalItems,
        totalPages: Math.ceil(fetchedPagination.totalItems / prev.itemsPerPage),
      }));
    }
  }, [fetchedPagination.totalItems, pagination.totalItems]);

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: Math.min(newPage, prev.totalPages),
    }));
  }, []);

  const createTeamMutation = useMutation({
    mutationFn: async (teamData: CreateTeamsInput) => {
      const createdTeam = await teamAPI.create(
        {
          ...teamData,
          start_date: new Date(teamData.start_date),
          end_date: new Date(teamData.end_date),
        },
        token
      );
      return createdTeam; // Return the created team
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      refetch();
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: async ({ id, ...teamData }: UpdateTeamInput) => {
      const updatedTeam = await teamAPI.update(
        id,
        {
          ...teamData,
          start_date: new Date(teamData.start_date),
          end_date: new Date(teamData.end_date),
        },
        token
      );
      return updatedTeam;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      refetch();
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (id: string) => {
      await teamAPI.delete(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      refetch();
    },
  });

  const getTeamById = useCallback(
    async (id: string): Promise<TeamDetails | null> => {
      try {
        const teamDetails = (await teamAPI.getById(id, token)) as TeamDetails;
        return teamDetails;
      } catch (err) {
        console.error("Error fetching team by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    teams,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination,
    setPagination,
    refetch,
    createTeam: createTeamMutation.mutateAsync,
    updateTeam: updateTeamMutation.mutateAsync,
    deleteTeam: deleteTeamMutation.mutateAsync,
    getTeamById,
    changePage,
    isCreating: createTeamMutation.isPending,
    isUpdating: updateTeamMutation.isPending,
    isDeleting: deleteTeamMutation.isPending,
  };
};
